/**
 * @page /portal/rechnungen
 * @description Kunden-Rechnungen — Rechnungsuebersicht mit Filterung, Detail-Modal
 *   und PDF-Download fuer Kunden im Portal.
 * @features
 *   - Rechnungsliste mit Status-Badges (offen, bezahlt, ueberfaellig)
 *   - Zeitraum- und Status-Filter
 *   - Detail-Modal mit Positionen
 *   - PDF-Download einzelner Rechnungen
 *   - Summen-KPIs (Gesamt, Offen, Bezahlt)
 * @api GET /api/portal/rechnungen, GET /api/portal/rechnungen/:id
 * @api GET /api/portal/rechnungen/:id/pdf
 * @roles kunde
 * @i18n portalInvoices
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader, StatusBadge } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';
const api = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());


function SummaryCard({ label, value, sub, color }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
      <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{sub}</div>}
    </div>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-surface-elevated border border-line-hover rounded-card shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-line sticky top-0 bg-surface-elevated z-10">
          <h3 className="text-sm font-semibold text-content">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-content-secondary transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function fmtEur(n) {
  if (n == null) return '0,00 €';
  return Number(n).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function DownloadIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function Spinner({ className }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

export default function PortalRechnungen() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'alle', von: '', bis: '' });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.status !== 'alle') params.set('status', filter.status);
    if (filter.von) params.set('von', filter.von);
    if (filter.bis) params.set('bis', filter.bis);
    api(`/api/portal/rechnungen?${params}`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter.status, filter.von, filter.bis]);

  const openDetail = async (rechnung) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await api(`/api/portal/rechnungen/${rechnung.id}`);
      setDetail(d);
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  const downloadPdf = async (id, e) => {
    if (e) e.stopPropagation();
    setPdfLoading(prev => ({ ...prev, [id]: true }));
    try {
      const resp = await fetch(`${API}/api/portal/rechnungen/${id}/pdf`, { credentials: 'include' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(err.error || t('customerPortal.invoices.pdfError'));
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert(t('customerPortal.invoices.pdfError'));
    } finally {
      setPdfLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const inp = "bg-surface-inset border border-line-input rounded-lg px-3 py-1.5 text-xs text-content focus:outline-none focus:border-primary-500/50 transition-colors";

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('customerPortal.invoices.title')} subtitle={t('customerPortal.invoices.subtitle')} />

      {/* Hinweis wenn keine Odoo-Verbindung */}
      {data?.hinweis && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-card px-4 py-3">
          <div className="text-xs text-yellow-400">{data.hinweis}</div>
        </div>
      )}

      {/* Zusammenfassung */}
      {data?.zusammenfassung && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryCard
            label={t('customerPortal.invoices.totalOpen')}
            value={fmtEur(data.zusammenfassung.gesamt_offen)}
            sub={t('customerPortal.invoices.nInvoices', { n: data.zusammenfassung.anzahl_offen })}
            color="#facc15"
          />
          <SummaryCard
            label={t('customerPortal.invoices.overdue')}
            value={fmtEur(data.zusammenfassung.gesamt_ueberfaellig)}
            sub={t('customerPortal.invoices.nInvoices', { n: data.zusammenfassung.anzahl_ueberfaellig })}
            color="#ef4444"
          />
          <SummaryCard
            label={t('customerPortal.invoices.lastPayment')}
            value={data.zusammenfassung.letzte_zahlung ? new Date(data.zusammenfassung.letzte_zahlung).toLocaleDateString('de-AT') : '–'}
            color="#34d399"
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} className={inp}>
          <option value="alle">{t('customerPortal.invoices.allStatuses')}</option>
          <option value="offen">{t('customerPortal.invoices.statusOpen')}</option>
          <option value="bezahlt">{t('customerPortal.invoices.statusPaid')}</option>
          <option value="ueberfaellig">{t('customerPortal.invoices.statusOverdue')}</option>
        </select>
        <input type="date" value={filter.von} onChange={e => setFilter(f => ({ ...f, von: e.target.value }))} className={inp} placeholder={t('customerPortal.invoices.from')} />
        <input type="date" value={filter.bis} onChange={e => setFilter(f => ({ ...f, bis: e.target.value }))} className={inp} placeholder={t('customerPortal.invoices.to')} />
      </div>

      {/* Tabelle */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.invoiceNr')}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.date')}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.due')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.amount')}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.paid')}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customerPortal.invoices.status')}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider w-12">{t('customerPortal.invoices.pdf')}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rechnungen || []).map(r => (
                  <tr
                    key={r.id}
                    onClick={() => openDetail(r)}
                    className="border-b border-line hover:bg-surface-hover cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-xs font-medium text-content">{r.nummer}</td>
                    <td className="px-4 py-3 text-xs text-content-tertiary">{r.datum ? new Date(r.datum).toLocaleDateString('de-AT') : '–'}</td>
                    <td className="px-4 py-3 text-xs text-content-tertiary">{r.faellig ? new Date(r.faellig).toLocaleDateString('de-AT') : '–'}</td>
                    <td className="px-4 py-3 text-xs text-content text-right font-mono">{fmtEur(r.betrag)}</td>
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)] text-right font-mono">{fmtEur(r.bezahlt)}</td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => downloadPdf(r.id, e)}
                        disabled={pdfLoading[r.id]}
                        className="text-[var(--text-secondary)] hover:text-primary-400 transition-colors disabled:opacity-50 p-1 rounded hover:bg-surface-hover"
                        title={t('customerPortal.invoices.downloadPdf')}
                      >
                        {pdfLoading[r.id] ? <Spinner className="h-4 w-4" /> : <DownloadIcon className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!data?.rechnungen || data.rechnungen.length === 0) && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-xs text-[var(--text-secondary)]">{t('customerPortal.invoices.noInvoices')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail-Modal */}
      <Modal open={!!detail || detailLoading} onClose={() => { setDetail(null); setDetailLoading(false); }} title={detail ? t('customerPortal.invoices.invoiceTitle', { nr: detail.nummer }) : t('customerPortal.invoices.loading')}>
        {detailLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
        ) : detail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[var(--text-secondary)]">{t('customerPortal.invoices.dateLabel')}</span> <span className="text-content ml-1">{detail.datum ? new Date(detail.datum).toLocaleDateString('de-AT') : '–'}</span></div>
              <div><span className="text-[var(--text-secondary)]">{t('customerPortal.invoices.dueLabel')}</span> <span className="text-content ml-1">{detail.faellig ? new Date(detail.faellig).toLocaleDateString('de-AT') : '–'}</span></div>
              <div><span className="text-[var(--text-secondary)]">{t('customerPortal.invoices.amountLabel')}</span> <span className="text-content ml-1 font-mono">{fmtEur(detail.betrag)}</span></div>
              <div><span className="text-[var(--text-secondary)]">{t('customerPortal.invoices.openLabel')}</span> <span className="text-content ml-1 font-mono">{fmtEur(detail.offen)}</span></div>
              <div><span className="text-[var(--text-secondary)]">{t('customerPortal.invoices.statusLabel')}</span> <StatusBadge status={detail.status} className="ml-1" /></div>
            </div>

            {/* PDF Download Button */}
            <button
              onClick={() => downloadPdf(detail.id)}
              disabled={pdfLoading[detail.id]}
              className="w-full flex items-center justify-center gap-2 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/20 text-primary-400 rounded-lg px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-50"
            >
              {pdfLoading[detail.id] ? (
                <><Spinner className="h-4 w-4" /> {t('customerPortal.invoices.pdfLoading')}</>
              ) : (
                <><DownloadIcon className="h-4 w-4" /> {t('customerPortal.invoices.downloadPdf')}</>
              )}
            </button>

            {detail.positionen && detail.positionen.length > 0 && (
              <>
                <div className="text-xs font-semibold text-[var(--text-secondary)] mt-4">{t('customerPortal.invoices.positions')}</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left py-1.5 text-[10px] text-[var(--text-secondary)]">{t('customerPortal.invoices.description')}</th>
                      <th className="text-right py-1.5 text-[10px] text-[var(--text-secondary)]">{t('customerPortal.invoices.quantity')}</th>
                      <th className="text-right py-1.5 text-[10px] text-[var(--text-secondary)]">{t('customerPortal.invoices.unitPrice')}</th>
                      <th className="text-right py-1.5 text-[10px] text-[var(--text-secondary)]">{t('customerPortal.invoices.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.positionen.map((pos, i) => (
                      <tr key={i} className="border-b border-line">
                        <td className="py-1.5 text-content-secondary max-w-[200px] truncate">{pos.bezeichnung}</td>
                        <td className="py-1.5 text-[var(--text-secondary)] text-right font-mono">{pos.menge}</td>
                        <td className="py-1.5 text-[var(--text-secondary)] text-right font-mono">{fmtEur(pos.einzelpreis)}</td>
                        <td className="py-1.5 text-content text-right font-mono">{fmtEur(pos.gesamt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
